import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaAngleLeft, FaAngleRight } from 'react-icons/fa';

const Customers = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 10; // Matches API's default limit

  // Fetch customers from API
  const fetchCustomers = async (page = 1, query = '') => {
    setLoading(true);
    try {
      const requestOptions = {
        method: 'GET',
        redirect: 'follow',
      };
      const url = query
        ? `https://api.alburaqscents.com/v1/customer?page=${page}&limit=${limit}&search=${encodeURIComponent(query)}`
        : `https://api.alburaqscents.com/v1/customer?page=${page}&limit=${limit}`;
      
      const response = await fetch(url, requestOptions);
      const result = await response.json();
      
      if (result.success) {
        setCustomers(result.data);
        setTotalPages(result.pagination.pages);
        setTotalResults(result.pagination.total);
      } else {
        console.error('Failed to fetch customers:', result.message);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and resize handler
  useEffect(() => {
    fetchCustomers(page, searchQuery);

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [page, searchQuery]);

  // Handle search input
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset to first page on search
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Styles (unchanged, included for completeness)
  const styles = {
    mainWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: 'transparent',
      padding: isSmallMobile ? '0px' : isMobile ? '15px' : '20px',
      fontFamily: 'Inter, sans-serif',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '1200px',
      backgroundColor: 'transparent',
      padding: isSmallMobile ? '15px' : '20px',
    },
    headerTitle: {
      fontSize: isSmallMobile ? '20px' : isMobile ? '24px' : '30px',
      color: '#000',
      marginBottom: isSmallMobile ? '15px' : '20px',
      fontWeight: 'bold',
      textAlign: 'left',
    },
    contentContainer: {
      backgroundColor: '#000',
      borderRadius: '10px',
      padding: isSmallMobile ? '15px' : '20px',
      color: '#E5D0A5',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px',
      flexDirection: isSmallMobile ? 'column' : 'row',
      gap: isSmallMobile ? '10px' : '0',
    },
    searchBar: {
      flexGrow: 1,
      minWidth: '200px',
      maxWidth: '400px',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'transparent',
      borderRadius: '24px',
      border: '1px solid #D4AF37',
      padding: '10px 20px',
      height: '50px',
    },
    searchIcon: {
      color: '#D4AF37',
      fontSize: '17px',
      marginRight: '10px',
    },
    searchInput: {
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      color: '#D4AF37',
      fontSize: '17px',
      flexGrow: 1,
      width: '100%',
    },
    exportButton: {
      backgroundColor: '#D4AF37',
      color: '#000',
      border: 'none',
      borderRadius: '5px',
      padding: '8px 15px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 'bold',
      transition: 'background-color 0.2s ease',
    },
    tableWrapper: {
      overflowX: 'auto',
      marginBottom: '15px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '700px' : '850px',
    },
    th: {
      padding: '16px 10px',
      whiteSpace: 'nowrap',
      textAlign: 'left',
      borderBottom: '1px solid #333',
      fontSize: '12px',
    },
    td: {
      padding: '16px 10px',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      fontSize: '12px',
      borderBottom: '1px solid #333',
    },
    rowHover: {
      transition: 'background-color 0.2s ease',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '15px',
      flexDirection: isSmallMobile ? 'column' : 'row',
      gap: isSmallMobile ? '10px' : '0',
    },
    paginationControls: {
      display: 'flex',
      gap: '4px',
      flexWrap: 'wrap',
      justifyContent: isSmallMobile ? 'center' : 'flex-start',
    },
    paginationButton: {
      backgroundColor: '#E5D0A5',
      color: '#000',
      border: '1px solid #000',
      borderRadius: '5px',
      padding: '4px 8px',
      cursor: 'pointer',
      fontSize: '12px',
      transition: 'background-color 0.2s ease',
      minWidth: '25px',
      textAlign: 'center',
    },
    activePage: {
      backgroundColor: '#000',
      color: '#E5D0A5',
    },
    paginationText: {
      color: '#E5D0A5',
      fontSize: '12px',
      marginTop: isSmallMobile ? '8px' : '0',
    },
  };

  return (
    <div style={styles.mainWrapper}>
      <div style={styles.container}>
        <h1 style={styles.headerTitle}>Customers</h1>

        <div style={styles.contentContainer}>
          <div style={styles.header}>
            <div style={styles.searchBar}>
              <FaSearch style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search"
                style={styles.searchInput}
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
            <button style={styles.exportButton}>Export</button>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Location</th>
                  <th style={styles.th}>Orders</th>
                  <th style={styles.th}>Spent</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" style={{ ...styles.td, textAlign: 'center' }}>
                      Loading...
                    </td>
                  </tr>
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ ...styles.td, textAlign: 'center' }}>
                      No customers found
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <tr
                      key={customer._id}
                      style={styles.rowHover}
                    >
                      <td style={styles.td}>{customer.name || 'N/A'}</td>
                      <td style={styles.td}>{customer.shippingAddress?.city || 'N/A'}</td>
                      <td style={styles.td}>{customer.totalOrders}</td>
                      <td style={styles.td}>Rs {customer.totalSpent}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={styles.pagination}>
            <div style={styles.paginationControls}>
              <button
                style={styles.paginationButton}
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <FaAngleLeft />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  style={{
                    ...styles.paginationButton,
                    ...(pageNum === page ? styles.activePage : {}),
                  }}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
              {totalPages > 5 && (
                <span style={{ ...styles.paginationText, margin: '0 5px' }}>...</span>
              )}
              {totalPages > 5 && (
                <button
                  style={styles.paginationButton}
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>
              )}
              <button
                style={styles.paginationButton}
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                <FaAngleRight />
              </button>
            </div>
            <p style={styles.paginationText}>{totalResults} Results</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;